import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  IndianRupee,
  LayoutTemplate,
  Loader2,
  Pencil,
  Search,
  Sparkles,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { useEmployees } from "../../../../../hooks/useEmployees";
import { findEmployeeById, formatEmployeeLabel } from "../../../../../components/common/employeeUtils";
import { useToast } from "../../../../Context/ToastContext";
import { formatInr } from "./pipelineCtcUtils";
import { readPipelineState, writePipelineState } from "./salaryTemplatePipeline";

function employeeDept(emp) {
  return emp?.department?.name || emp?.department_name || "—";
}

function employeeDesignation(emp) {
  return emp?.designation?.name || emp?.designation_name || emp?.job_title || "—";
}

function avatarHue(name) {
  let hash = 0;
  const s = String(name || "");
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  const hues = ["#4f46e5", "#0f766e", "#0369a1", "#7c3aed", "#c2410c", "#15803d"];
  return hues[Math.abs(hash) % hues.length];
}

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function defaultCtcForEmployee(emp, pipelineTarget) {
  const fromProfile = emp?.annual_ctc;
  if (fromProfile != null && fromProfile !== "" && Number(fromProfile) > 0) {
    return String(fromProfile);
  }
  if (pipelineTarget != null && Number(pipelineTarget) > 0) {
    return String(pipelineTarget);
  }
  return "";
}

function normalizeDraftRow(emp, ctc, templateName) {
  const id = String(emp?.employee_id || emp?.id || "");
  return {
    employee_id: id,
    employee_name: formatEmployeeLabel(emp) || emp?.employee_name || "",
    employee_code: emp?.employee_code || "",
    department: employeeDept(emp),
    designation: employeeDesignation(emp),
    ctc: ctc != null && ctc !== "" ? Number(ctc) : null,
    template_name: templateName,
  };
}

function StatusBadge({ status }) {
  if (status === "balanced") {
    return (
      <span className="stp-alloc-status stp-alloc-status--ok">
        <CheckCircle2 size={13} aria-hidden /> Structure target
      </span>
    );
  }
  if (status === "custom") {
    return (
      <span className="stp-alloc-status stp-alloc-status--custom">
        <CheckCircle2 size={13} aria-hidden /> Custom package
      </span>
    );
  }
  if (status === "missing" || status === "under" || status === "over") {
    return (
      <span className="stp-alloc-status stp-alloc-status--warn">
        <AlertCircle size={13} aria-hidden /> Enter annual salary
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="stp-alloc-status stp-alloc-status--bad">
        <AlertCircle size={13} aria-hidden /> Check
      </span>
    );
  }
  return <span className="stp-alloc-status stp-alloc-status--muted">Pending</span>;
}

export default function PipelineReviewEmployeeAllocation({
  templateName,
  targetAnnualCtc,
  assignedRows,
  employeeBalance,
  onAssignmentsChange,
}) {
  const toast = useToast();
  const { employees, loading: employeesLoading, refresh: refreshEmployees } = useEmployees();
  const [search, setSearch] = useState("");
  const [pickerOpen, setPickerOpen] = useState(true);
  const [draft, setDraft] = useState([]);

  useEffect(() => {
    refreshEmployees();
  }, [refreshEmployees]);

  useEffect(() => {
    const state = readPipelineState();
    const fromState = state.draftEmployeeAssignments || [];
    if (fromState.length) {
      setDraft(fromState);
      return;
    }
    if (assignedRows?.length) {
      setDraft(assignedRows);
    }
  }, [assignedRows]);

  const assignedIdSet = useMemo(
    () => new Set(draft.map((r) => String(r.employee_id)).filter(Boolean)),
    [draft]
  );

  const persist = useCallback(
    (nextDraft) => {
      setDraft(nextDraft);
      writePipelineState({ draftEmployeeAssignments: nextDraft });
      onAssignmentsChange?.(nextDraft);
    },
    [onAssignmentsChange]
  );

  const filteredAvailable = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = (employees || []).filter((emp) => {
      const id = String(emp?.employee_id || emp?.id || "");
      return id && !assignedIdSet.has(id);
    });
    if (q) {
      list = list.filter((emp) => {
        const label = formatEmployeeLabel(emp).toLowerCase();
        const code = String(emp?.employee_code || "").toLowerCase();
        return label.includes(q) || code.includes(q) || employeeDept(emp).toLowerCase().includes(q);
      });
    }
    return list;
  }, [employees, search, assignedIdSet]);

  const enrichedDraft = useMemo(
    () =>
      draft.map((row) => {
        const emp = findEmployeeById(employees, row.employee_id);
        const name = row.employee_name || formatEmployeeLabel(emp) || "Employee";
        return {
          ...row,
          employee_name: name,
          employee_code: row.employee_code || emp?.employee_code || "",
          department: row.department || employeeDept(emp),
          designation: row.designation || employeeDesignation(emp),
          initials: getInitials(name),
          hue: avatarHue(name),
        };
      }),
    [draft, employees]
  );

  const stats = useMemo(() => {
    let ready = 0;
    let custom = 0;
    let needsWork = 0;
    enrichedDraft.forEach((row) => {
      const st = employeeBalance[String(row.employee_id)];
      if (st === "balanced" || st === "custom") ready += 1;
      if (st === "custom") custom += 1;
      if (st === "missing") needsWork += 1;
    });
    return {
      assigned: enrichedDraft.length,
      ready,
      custom,
      balanced: ready,
      needsWork,
      available: (employees || []).length - assignedIdSet.size,
    };
  }, [enrichedDraft, employeeBalance, employees, assignedIdSet.size]);

  const tplLabel = templateName?.trim() || "Untitled template (draft)";
  const hasTarget = Number(targetAnnualCtc) > 0;

  function addEmployee(emp) {
    const id = String(emp?.employee_id || emp?.id || "");
    if (!id || assignedIdSet.has(id)) return;
    const ctcStr = defaultCtcForEmployee(emp, targetAnnualCtc);
    const row = normalizeDraftRow(
      emp,
      ctcStr || (targetAnnualCtc != null ? targetAnnualCtc : ""),
      templateName
    );
    persist([...draft, row]);
    if (!ctcStr && !targetAnnualCtc) {
      toast.success("Employee added — set annual gross salary in the package row.");
    } else {
      toast.success(`${formatEmployeeLabel(emp) || "Employee"} added.`);
    }
  }

  function removeEmployee(employeeId) {
    persist(draft.filter((r) => String(r.employee_id) !== String(employeeId)));
  }

  function updateRowCtc(employeeId, value) {
    const cleaned = String(value || "").replace(/,/g, "");
    persist(
      draft.map((r) =>
        String(r.employee_id) === String(employeeId)
          ? { ...r, ctc: cleaned === "" ? null : Number(cleaned) }
          : r
      )
    );
  }

  function applyTargetToAll() {
    const t = Number(targetAnnualCtc);
    if (!Number.isFinite(t) || t <= 0) {
      toast.error("Set gross salary in the gross salary step first.");
      return;
    }
    persist(draft.map((r) => ({ ...r, ctc: t })));
    toast.success(`Same annual gross (${formatInr(t)}) applied to all selected employees.`);
  }

  function focusEmployeeSalary(employeeId) {
    const el = document.getElementById(`stp-alloc-ctc-${employeeId}`);
    if (!el) return;
    el.focus();
    el.select?.();
  }

  return (
    <div className="stp-alloc">
      <div className="stp-alloc-hero">
        <div className="stp-alloc-hero-main">
          <span className="stp-alloc-hero-icon" aria-hidden>
            <Users size={20} />
          </span>
          <div>
            <p className="stp-alloc-eyebrow">
              <Sparkles size={12} /> Package assignment
            </p>
            <h4 className="stp-alloc-title">Assign employees to this structure</h4>
            <p className="stp-alloc-sub">
              Choose team members, set each annual gross salary, and save the structure.
            </p>
          </div>
        </div>
        <div className="stp-alloc-tpl-chip" title="Salary structure name">
          <LayoutTemplate size={15} aria-hidden />
          <span>{tplLabel}</span>
        </div>
      </div>

      <div className="stp-alloc-stats">
        <div className="stp-alloc-stat">
          <span className="stp-alloc-stat-label">Selected</span>
          <strong>{stats.assigned}</strong>
        </div>
        <div className="stp-alloc-stat stp-alloc-stat--ok">
          <span className="stp-alloc-stat-label">Ready</span>
          <strong>{stats.ready}</strong>
        </div>
        <div className="stp-alloc-stat stp-alloc-stat--custom">
          <span className="stp-alloc-stat-label">Custom packages</span>
          <strong>{stats.custom}</strong>
        </div>
        <div className="stp-alloc-stat stp-alloc-stat--warn">
          <span className="stp-alloc-stat-label">Need salary</span>
          <strong>{stats.needsWork}</strong>
        </div>
        <div className="stp-alloc-stat">
          <span className="stp-alloc-stat-label">Gross salary</span>
          <strong>{hasTarget ? formatInr(targetAnnualCtc) : "—"}</strong>
        </div>
      </div>

      <div className={`stp-alloc-grid${pickerOpen ? " is-picker-open" : ""}`}>
        <section className="stp-alloc-panel stp-alloc-panel--assigned" aria-labelledby="stp-alloc-assigned-title">
          <header className="stp-alloc-panel-head">
            <div>
              <h5 id="stp-alloc-assigned-title">Selected employees</h5>
              <p className="stp-alloc-panel-hint">
                {stats.assigned
                  ? "Each employee can have a different annual package. Use the pencil to edit, then save the structure."
                  : "No one selected yet — add employees from the panel on the right."}
              </p>
            </div>
          </header>

          {enrichedDraft.length === 0 ? (
            <div className="stp-alloc-empty">
              <div className="stp-alloc-empty-icon" aria-hidden>
                <UserPlus size={28} />
              </div>
              <p className="stp-alloc-empty-title">Build your assignment list</p>
              <p className="stp-alloc-empty-text">
                Search active employees and add them to <strong>{tplLabel}</strong>.
              </p>
              <button
                type="button"
                className="stp-btn stp-btn-primary"
                onClick={() => setPickerOpen(true)}
              >
                <UserPlus size={16} /> Browse employees
              </button>
            </div>
          ) : (
            <>
              <ul className="stp-alloc-roster">
                {enrichedDraft.map((row) => {
                  const key = String(row.employee_id);
                  const annual = Number(row.ctc ?? targetAnnualCtc);
                  const monthly =
                    Number.isFinite(annual) && annual > 0 ? Math.round(annual / 12) : null;
                  const st = employeeBalance[key];
                  return (
                    <li key={key} className="stp-alloc-roster-item">
                      <div
                        className="stp-alloc-avatar"
                        style={{
                          background: `linear-gradient(135deg, ${row.hue}18, ${row.hue}38)`,
                          color: row.hue,
                        }}
                      >
                        {row.initials}
                      </div>
                      <div className="stp-alloc-roster-main">
                        <div className="stp-alloc-roster-top">
                          <div>
                            <span className="stp-alloc-emp-name">{row.employee_name}</span>
                            <span className="stp-alloc-emp-meta">
                              {row.employee_code || "—"} · {row.department}
                              {row.designation !== "—" ? ` · ${row.designation}` : ""}
                            </span>
                          </div>
                          <StatusBadge status={st} />
                        </div>
                        <div className="stp-alloc-roster-fields">
                          <label className="stp-alloc-field" htmlFor={`stp-alloc-ctc-${key}`}>
                            <span className="stp-alloc-field-label">Annual</span>
                            <span className="stp-alloc-input-wrap">
                              <IndianRupee size={14} className="stp-alloc-input-icon" aria-hidden />
                              <input
                                id={`stp-alloc-ctc-${key}`}
                                className="stp-alloc-input"
                                inputMode="numeric"
                                value={row.ctc != null && row.ctc !== "" ? String(row.ctc) : ""}
                                placeholder={hasTarget ? String(targetAnnualCtc) : "600000"}
                                onChange={(e) => updateRowCtc(row.employee_id, e.target.value)}
                                aria-label={`Annual gross salary for ${row.employee_name}`}
                              />
                            </span>
                          </label>
                          <div className="stp-alloc-field stp-alloc-field--readonly">
                            <span className="stp-alloc-field-label">Monthly</span>
                            <span className="stp-alloc-readout">
                              {monthly != null ? formatInr(monthly) : "—"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="stp-alloc-roster-actions">
                        <button
                          type="button"
                          className="stp-alloc-edit"
                          title="Edit salary for this employee"
                          aria-label={`Edit salary for ${row.employee_name}`}
                          onClick={() => focusEmployeeSalary(key)}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          className="stp-alloc-remove"
                          title="Remove from this structure"
                          aria-label={`Remove ${row.employee_name}`}
                          onClick={() => removeEmployee(row.employee_id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {hasTarget ? (
                <div className="stp-alloc-bulk-bar">
                  <div className="stp-alloc-bulk-copy">
                    <strong>Optional — use structure target for everyone</strong>
                    <span>
                      Each employee can keep a different annual package. Only use this if you want
                      everyone set to {formatInr(targetAnnualCtc)}.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="stp-btn stp-btn-ghost stp-alloc-bulk-btn"
                    onClick={applyTargetToAll}
                  >
                    <Sparkles size={16} aria-hidden />
                    Use structure salary for all
                  </button>
                </div>
              ) : null}
            </>
          )}
        </section>

        <section
          className={`stp-alloc-panel stp-alloc-panel--picker${pickerOpen ? " is-open" : ""}`}
          aria-labelledby="stp-alloc-picker-title"
        >
          <header className="stp-alloc-panel-head">
            <div>
              <h5 id="stp-alloc-picker-title">Add employees</h5>
              <p className="stp-alloc-panel-hint">
                {stats.available > 0
                  ? `${stats.available} available · search by name or code`
                  : "All active employees are already selected."}
              </p>
            </div>
            <button
              type="button"
              className="stp-alloc-link-btn"
              onClick={() => setPickerOpen((v) => !v)}
              aria-expanded={pickerOpen}
            >
              {pickerOpen ? "Collapse" : "Expand"}
            </button>
          </header>

          {pickerOpen ? (
            <>
              <div className="stp-alloc-search">
                <Search size={16} aria-hidden />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, employee ID, department…"
                  aria-label="Search employees"
                />
              </div>

              {employeesLoading ? (
                <div className="stp-alloc-loading">
                  <Loader2 size={20} className="stp-spin" />
                  <span>Loading employees…</span>
                </div>
              ) : (
                <ul className="stp-alloc-browse">
                  {filteredAvailable.slice(0, 40).map((emp) => {
                    const id = String(emp.employee_id || emp.id);
                    const label = formatEmployeeLabel(emp);
                    const suggested = defaultCtcForEmployee(emp, targetAnnualCtc);
                    const hue = avatarHue(label);
                    return (
                      <li key={id}>
                        <div
                          className="stp-alloc-avatar stp-alloc-avatar--sm"
                          style={{
                            background: `linear-gradient(135deg, ${hue}18, ${hue}38)`,
                            color: hue,
                          }}
                        >
                          {getInitials(label)}
                        </div>
                        <div className="stp-alloc-browse-main">
                          <span className="stp-alloc-emp-name">{label}</span>
                          <span className="stp-alloc-emp-meta">
                            <span className="stp-alloc-code">{emp.employee_code || "—"}</span>
                            <span className="stp-alloc-dept">{employeeDept(emp)}</span>
                            {suggested ? (
                              <span className="stp-alloc-suggest">Gross salary {formatInr(suggested)}</span>
                            ) : null}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="stp-alloc-add-btn"
                          onClick={() => addEmployee(emp)}
                        >
                          <UserPlus size={14} /> Add
                        </button>
                      </li>
                    );
                  })}
                  {!filteredAvailable.length ? (
                    <li className="stp-alloc-browse-empty">
                      {search.trim() ? "No matches — try another search." : "No employees left to add."}
                    </li>
                  ) : null}
                </ul>
              )}
            </>
          ) : (
            <button
              type="button"
              className="stp-btn stp-btn-ghost stp-alloc-expand-btn"
              onClick={() => setPickerOpen(true)}
            >
              <UserPlus size={16} /> Open employee browser
            </button>
          )}
        </section>
      </div>
    </div>
  );
}
